import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../styles/TranscriptModal.css';

const TranscriptModal = ({ transcript, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!transcript) return; // Only apply scroll lock if transcript exists
    
    // Store the original overflow value
    const originalOverflow = document.body.style.overflow;
    
    // Disable scrolling
    document.body.style.overflow = 'hidden';
    
    // Cleanup function to restore original overflow when modal closes
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [transcript]);

  if (!transcript) return null;

  // Parse the transcript text into conversation format
  const parseTranscript = (text) => {
    if (!text) return [];
    
    try {
      // The transcript comes as a string with escaped JSON objects
      // First, let's try to extract JSON objects from the string
      
      // Look for patterns like {"message":"...","sender":"..."}
      const jsonPattern = /\{\\?"message\\?":\\?"([^"\\]+)\\?",\\?"sender\\?":\\?"(user|assistant)\\?"\}/g;
      const matches = [];
      let match;
      
      while ((match = jsonPattern.exec(text)) !== null) {
        matches.push({
          message: match[1],
          sender: match[2]
        });
      }
      
      
      if (matches.length > 0) {
        return matches.map((messageObj, index) => ({
          id: index,
          text: messageObj.message || '',
          isAssistant: messageObj.sender === 'assistant'
        }));
      }
      
      // If regex parsing fails, try to parse as JSON array
      const parsedArray = JSON.parse(text);
      
      if (Array.isArray(parsedArray)) {
        return parsedArray.map((item, index) => {
          try {
            // Each item is a stringified JSON object
            const messageObj = typeof item === 'string' ? JSON.parse(item) : item;
            return {
              id: index,
              text: messageObj.message || '',
              isAssistant: messageObj.sender === 'assistant'
            };
          } catch (err) {
            console.error('Error parsing message item:', err);
            return {
              id: index,
              text: typeof item === 'string' ? item : JSON.stringify(item),
              isAssistant: index % 2 === 0
            };
          }
        });
      }
    } catch (err) {
      console.error('Error parsing transcript:', err);
      
      // Fallback to old parsing method if JSON parsing fails
      const lines = text.split(/\n|(?:Assistant|User|AI|Human|Bot):\s*/i);
      
      return lines
        .filter(line => line.trim())
        .map((line, index) => {
          const trimmed = line.trim();
          
          const isAssistant = index % 2 === 0 || 
                             trimmed.toLowerCase().includes('שלום') ||
                             trimmed.toLowerCase().includes('איך אני יכול') ||
                             trimmed.toLowerCase().includes('תודה שפנית');
          
          return {
            id: index,
            text: trimmed,
            isAssistant
          };
        });
    }
    
    return [];
  };

  const messages = parseTranscript(transcript.transcript);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="transcript-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="call-info">
            <h3>שיחה עם {transcript.userName || 'אורח לא ידוע'}</h3>
            <p className="call-details">
              {transcript.userPhone} • {formatDate(transcript.createdAt)}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="chat-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>לא נמצא תוכן שיחה</p>
            </div>
          ) : (
            <div className="messages">
              {messages.map((message) => (
                <div 
                  key={message.id}
                  className={`message ${message.isAssistant ? 'assistant' : 'user'}`}
                >
                  <div className="message-bubble">
                    <p>{message.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TranscriptModal;
